/**
 * Turning a machine's queue into what an operator actually sees.
 *
 * The API returns `groups[]`, one per job, each with its own `queue[]`. That is
 * the wrong shape for a screen: a machine runs ONE ordered list, and two things
 * in it are not separate rows even though they are separate entries.
 *
 * Three levels, in this order, and all three are needed or the display lies:
 *
 *   1. FLATTEN   every job's queue into one list ordered by `_queueSeq` — the
 *                real order the machine will run them in.
 *
 *   2. BLOCK     adjacent entries that share the SAME output token are one
 *                merge-execution: two products extruded onto one drum, on one
 *                pass. Different tasks, one physical thing.
 *
 *   3. RUN       consecutive blocks sharing the same `GB::<phase>::<kind>`
 *                prefix are one group-by launch: same colour, or same wire
 *                diameter, queued together deliberately so the machine does not
 *                change over between them. NOT a merge — each has its own token
 *                and its own bobbin — but the operator is meant to see them as
 *                one run with one heading, which is what the HMI does.
 *
 * Level 3 is the one that keeps getting left out, and it is invisible when it
 * is: the rows are all there, in the right order, just not joined up — so a
 * group-by-wire-diameter launch reads as unrelated executions that happen to be
 * next to each other.
 *
 * Pure and dependency-free: the same functions feed the queue list, the queue
 * editor and the Factory-3D machine panel, so the three cannot drift. The HMI
 * pages implement the same three levels in vanilla JS (no build step there —
 * they cannot import this), so they stay a deliberate duplicate.
 */

// ── job colours ─────────────────────────────────────────────────────────────
// Stable per job, so one product keeps the same stripe everywhere it appears.

export const JOB_COLOR_PALETTE = [
  '#4e79a7',
  '#f28e2b',
  '#e15759',
  '#76b7b2',
  '#59a14f',
  '#edc948',
  '#b07aa1',
  '#ff9da7',
  '#9c755f',
  '#bab0ab',
  '#2f7cff',
  '#8b5a2b',
  '#9acd32',
  '#d9534f',
  '#2e8b57',
  '#f0c400'
];

export function hashString(str) {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

export function getJobColor(jobId) {
  if (!jobId) return '#8892a0';
  const idx = hashString(jobId) % JOB_COLOR_PALETTE.length;
  return JOB_COLOR_PALETTE[idx];
}

export function getBlockColor(block) {
  const first = block?.items?.[0];
  const jobId = first?.group?.jobId || '';
  return getJobColor(jobId);
}

// ── level 2: merge-execution blocks ─────────────────────────────────────────

export function getPrimaryOutput(task) {
  const outputs = task?.outputs || [];
  return outputs.length ? outputs[0] : null;
}

export function getPrimaryTokenId(task) {
  const out = getPrimaryOutput(task);
  return out?.tokenId || null;
}

/**
 * Are these two queue entries the same physical execution?
 *
 * Only when they write to the SAME output token — that is what a merge is. Two
 * entries of a group-by run have different tokens (a bobbin each) and must stay
 * separate rows; they are joined at level 3 instead.
 */
export function sameSharedExecution(a, b) {
  if (!a || !b || !a.task || !b.task) return false;

  const tokenA = getPrimaryTokenId(a.task);
  const tokenB = getPrimaryTokenId(b.task);

  if (!tokenA || !tokenB) return false;
  if (tokenA !== tokenB) return false;

  // must be DIFFERENT tasks pointing to the SAME token (true merge)
  if (String(a.task.taskId || '') === String(b.task.taskId || '')) return false;

  return true;
}

// ── level 1: one machine, one order ─────────────────────────────────────────

export function flattenGroups(groups) {
  const rows = [];

  for (const group of groups || []) {
    const queue = group?.queue || [];
    for (const task of queue) {
      rows.push({
        group,
        task,
        _queueSeq: Number(task?._queueSeq)
      });
    }
  }

  rows.sort((a, b) => {
    let sa = Number(a?._queueSeq);
    let sb = Number(b?._queueSeq);

    // entries with no sequence sink to the end rather than jumping to the front,
    // which is what a NaN comparison would otherwise do
    if (!Number.isFinite(sa)) sa = 999999999;
    if (!Number.isFinite(sb)) sb = 999999999;

    return sa - sb;
  });

  return rows;
}

export function buildDisplayBlocks(groups) {
  const flat = flattenGroups(groups);
  const blocks = [];
  let current = null;

  for (const item of flat) {
    if (!current) {
      current = { items: [item] };
      continue;
    }

    if (sameSharedExecution(current.items[current.items.length - 1], item)) {
      current.items.push(item);
    } else {
      blocks.push(current);
      current = { items: [item] };
    }
  }

  if (current) blocks.push(current);
  return blocks;
}

export function uniqueProductsFromBlock(block) {
  const seen = {};
  const list = [];

  for (const item of block?.items || []) {
    const g = item.group || {};
    const jobId = String(g.jobId || '');
    const key = jobId;

    if (!seen[key]) {
      seen[key] = true;
      list.push({
        key,
        jobId,
        productLabel: g.productLabel || g.name || '-',
        section: g.section ?? null,
        greenYellowSection: g.greenYellowSection ?? null,
        plannedLength: g.plannedLength,
        // Carried so a screen can say who the drum is FOR without a second
        // lookup: `deriveDestination(allocations, plannedLength)` turns these
        // into CLIENT / STOCK / MIXED. The operator panel in Factory 3D shows
        // it as a hint before the wizard is ever opened.
        allocations: Array.isArray(g.allocations) ? g.allocations : [],
        // the machine's phase for this drum → what the edit wizard opens
        phase: item.task?.phase || null
      });
    }
  }

  return list;
}

// ── level 3: group-by runs ──────────────────────────────────────────────────

/** `GB::phase::kind` prefix (colour/Ø-agnostic) identifying a whole launch. */
export function blockGroupByPrefix(block) {
  const key = block?.items?.[0]?.task?.groupByKey;
  if (!key) return null;
  const parts = String(key).split('::');
  if (parts.length < 4) return null;
  return parts.slice(0, 3).join('::');
}

/** `GB::phase::kind::value` → what the run is grouped BY, for the heading. */
export function parseGroupByKey(key) {
  if (!key) return null;
  const parts = String(key).split('::');
  if (parts.length < 4) return null;
  const kind = parts[2];
  // the value can itself contain "::" — rejoin rather than take parts[3]
  const value = parts.slice(3).join('::');
  if (kind === 'color') return { kind: 'color', value };
  if (kind === 'wd') return { kind: 'wireDiameter', value };
  return null;
}

/** The group-by descriptor of a block, or null when it is not part of a run. */
export function blockGroupBy(block) {
  return parseGroupByKey(block?.items?.[0]?.task?.groupByKey);
}

/**
 * Where each task sits in the order the machine will actually run it.
 *
 * Numbered by BLOCK, not by task, and that is the whole point: a
 * merge-execution is ONE pass of the machine, so both of its tasks carry the
 * SAME number. Two rows showing "#4" says "these run together" to somebody who
 * has never heard of a shared token — which is the audience that needs telling.
 *
 * For the per-product queue table, where each row lists its own tasks in its
 * own order and nothing relates the rows to each other, this is the only thing
 * that answers "what runs first".
 *
 * @returns {{ map: Map<string, number>, total: number }} 1-based positions
 */
export function buildTaskOrder(groups) {
  const blocks = buildDisplayBlocks(groups);
  const map = new Map();

  blocks.forEach((block, i) => {
    for (const item of block?.items || []) {
      const id = item?.task?.taskId;
      if (id) map.set(String(id), i + 1);
    }
  });

  return { map, total: blocks.length };
}

/**
 * The rows to render: one per block, each told whether it opens a group-by run
 * (so only the first shows the heading) and which colour stripe it carries.
 */
export function buildBlockRows(groups) {
  const blocks = buildDisplayBlocks(groups);

  // ── runs: consecutive blocks that belong to the same LOT ──────────────────
  //
  // One product header per run, on its first block. A run of forty drums of the
  // same launch names its products once at the top and then gets on with the
  // drums, which is what makes the table readable.
  //
  // Two things join consecutive blocks into one run, and BOTH are needed:
  //
  //   · the same `GB::phase::kind` prefix — a group-by launch is one job PER
  //     COLOUR, so its blocks have entirely different jobIds and nothing else
  //     would hold them together;
  //   · overlapping jobIds — a merge run is the same two jobs over and over,
  //     and the merged blocks carry no group-by key at all.
  //
  // A second lot of the same cable has different jobIds and no shared prefix, so
  // it correctly starts a new run and names itself again. Two lots of
  // "CYYF 5x6" are not one product with twenty drums.
  const jobIdsOfBlock = block =>
    new Set(
      (block?.items || [])
        .map(it => it?.group?.jobId)
        .filter(Boolean)
        .map(String)
    );
  const intersects = (a, b) => [...a].some(x => b.has(x));

  // TWO groupings, because two different questions are being asked.
  //
  // 1. THE GROUP-BY RUN — the visual bracket: shared border, one "grouped run ·
  //    BLUE" heading. Keyed on the `GB::phase::kind` prefix and nothing else, so
  //    an ungrouped drum in the middle ENDS it: a bracket that reaches around a
  //    drum which is not part of the grouping says something untrue.
  //
  // 2. THE LOT — who the successive drums belong to, which is what the product
  //    header answers. A launch of 5x6 + 4x6 is forty drums of two products and
  //    the names belong at the top, once.
  //
  // These were one thing, and conflating them is what made the header repeat.
  const groupIdOf = [];
  const groupIsGB = [];
  let gid = -1;
  let prevPrefix = null;
  blocks.forEach((block, i) => {
    const pref = blockGroupByPrefix(block);
    if (!(pref && pref === prevPrefix)) {
      gid += 1;
      groupIsGB[gid] = !!pref;
    }
    groupIdOf[i] = gid;
    prevPrefix = pref;
  });

  // The lot: consecutive blocks joined by a shared group-by prefix OR by sharing
  // a job with the lot SO FAR — not merely with the block before.
  //
  // A lot's drums are not neatly interleaved: a 5x6 + 4x6 run can have four solo
  // 5x6 drums and then a solo 4x6 one. Comparing only with the previous block
  // broke the lot there, because that 4x6 shares no job with the 5x6 drum above
  // it, and the header appeared again for a lot that had already named itself.
  //
  // Keyed on jobIds, never on the label: two launches of "CYYF 5x6" are two lots
  // and each names itself.
  const lotIdOf = [];
  let lotId = -1;
  let lotJobs = new Set();
  let prevLotPrefix = null;
  blocks.forEach((block, i) => {
    const pref = blockGroupByPrefix(block);
    const jobs = jobIdsOfBlock(block);
    const sameLot =
      i > 0 && ((pref && pref === prevLotPrefix) || intersects(jobs, lotJobs));

    if (!sameLot) {
      lotId += 1;
      lotJobs = new Set(jobs);
    } else {
      for (const id of jobs) lotJobs.add(id);
    }
    lotIdOf[i] = lotId;
    prevLotPrefix = pref;
  });

  // Every product of the lot, distinct, in first-seen order. Before this the
  // header was keyed on each block's PRIMARY job, so the second product of a
  // merge — the CYYF 4x6 of 2026-10-19, 1 050 m beside a 13 300 m share of the
  // 5x6 — was never named anywhere on the page.
  const lotProducts = {};
  blocks.forEach((block, i) => {
    const arr = lotProducts[lotIdOf[i]] || (lotProducts[lotIdOf[i]] = []);
    for (const p of uniqueProductsFromBlock(block)) {
      if (!arr.some(x => x.jobId === p.jobId)) arr.push(p);
    }
  });

  const seenLots = new Set();

  return blocks.map((block, index) => {
    const g = groupIdOf[index];
    const isGB = groupIsGB[g];

    // The lot's whole product list, named ONCE, on its first block.
    const lot = lotIdOf[index];
    const products = lotProducts[lot] || uniqueProductsFromBlock(block);
    const primaryJobId = products?.[0]?.jobId || null;
    const showJob = !seenLots.has(lot);
    seenLots.add(lot);

    // group-by runs share one border colour (the run's primary job); merged /
    // solo blocks keep their own job colour.
    const borderColor = isGB ? getJobColor(primaryJobId) : getBlockColor(block);

    return {
      id: `block-${block.items.map(i => i.task?.taskId).join('|')}`,
      block,
      products,
      primaryJobId,
      showJob,
      borderColor,
      // merged (shared token) OR group-by run → editing ONE product alone would
      // break the group, so the per-product Edit button is hidden (use the top
      // "Edit selected together" instead).
      isGrouped: isGB || (block?.items?.length || 0) > 1,
      // which run this block belongs to, and what it is grouped by — the list
      // and the 3D panel need it to draw the run heading
      groupRunId: g,
      isGroupByRun: isGB,
      groupBy: isGB ? blockGroupBy(block) : null,
      // Which LOT — the successive drums of one launch. Not the same thing as
      // `groupRunId`: an ungrouped drum in the middle ends a group-by BRACKET
      // without starting a new lot, and the product header follows the lot.
      lotRunId: lot,
      blockIndex: index
    };
  });
}
